'use client';

import type { UseQueryOptions } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { contentfulClient } from '../';
import type {
  ReleaseNote,
  ReleaseNoteSkeleton,
  ReleaseNotesQueryOptions,
} from '../types';
import { transformReleaseNoteEntry } from '../utils';

/**
 * Fetches release notes from Contentful
 *
 * @param options - Query options including filters, pagination, and ordering
 * @returns Array of release notes
 */
async function fetchReleaseNotes(
  options: ReleaseNotesQueryOptions = {}
): Promise<ReleaseNote[]> {
  // Return empty array if Contentful is not configured
  if (!contentfulClient) {
    console.warn(
      'Contentful client is not configured. Returning empty release notes.'
    );
    return [];
  }

  const { limit = 10, skip = 0, order = '-fields.date', category } = options;

  const query: Record<string, unknown> = {
    content_type: 'releaseNote',
    limit,
    skip,
    order,
  };

  if (category) {
    query['fields.categories[in]'] = category;
  }

  try {
    const response =
      await contentfulClient.getEntries<ReleaseNoteSkeleton>(query);

    // Transform entries and filter out any that fail validation
    const notes: ReleaseNote[] = [];

    for (const entry of response.items) {
      try {
        notes.push(transformReleaseNoteEntry(entry));
      } catch (error) {
        console.error('Failed to transform release note entry:', error);
        // Skip this entry and continue with others
      }
    }

    return notes;
  } catch (error) {
    console.error('Failed to fetch release notes from Contentful:', error);
    return [];
  }
}

/**
 * Hook to fetch a list of release notes from Contentful
 *
 * @param queryOptions - Options for filtering, pagination, and ordering release notes
 * @param reactQueryOptions - Additional React Query options
 * @returns UseQueryResult containing release notes data
 *
 * @example
 * ```tsx
 * const { data: releaseNotes, isLoading } = useGetReleaseNotes({
 *   limit: 5,
 *   order: '-fields.date',
 *   category: 'features'
 * });
 * ```
 */
export function useGetReleaseNotes(
  queryOptions: ReleaseNotesQueryOptions = {},
  reactQueryOptions?: Omit<
    UseQueryOptions<ReleaseNote[], Error>,
    'queryKey' | 'queryFn'
  >
) {
  return useQuery<ReleaseNote[], Error>({
    queryKey: ['contentful', 'release-notes', queryOptions],
    queryFn: () => fetchReleaseNotes(queryOptions),
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false,
    ...reactQueryOptions,
  });
}
